import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { CommentRow } from '../row';
import useUsersStore from '../../../../../hooks/useUsersStore';
import useCommonStore from '../../../../../hooks/useCommonStore';
import IsLogin from '../../../../../components/isLogin';
import { useHistory } from 'react-router-dom';
// import '../../../../../../src/style/layout.scss';
import styled from 'styled-components';
import useTypeStore from "hooks/useTypeStore";

const CommentArea = styled.div`
  padding: 20px;
  background-color: #f9f9f9;
`;

// const Pagination = styled.div`
//   display: flex;
//   justify-content: center;
//   margin-top: 20px;
//   gap: 6px;
// `;
//
// const PageButton = styled.button`
//   padding: 6px 12px;
//   border: 1px solid #ccc;
//   background: white;
//   cursor: pointer;
//   &.active {
//     background: #333;
//     color: white;
//     font-weight: bold;
//   }
//   &:disabled {
//     opacity: 0.5;
//     cursor: not-allowed;
//   }
// `;
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 20px;

  /* critical */
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  flex-wrap: nowrap;

  /* smooth UX */
  scroll-behavior: smooth;

  /* hide scrollbar */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PageButton = styled.button`
  padding: 6px 10px;
  min-width: 32px;
  border: 1px solid #ccc;
  background: white;
  cursor: pointer;
  flex-shrink: 0; /* IMPORTANT */

  &.active {
    background: #333;
    color: white;
    font-weight: bold;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;


// const getPageRange = (current, total, delta = 2) => {
//   const range = [];
//   const pages = [];
//   let last;
//
//   for (let i = 1; i <= total; i++) {
//     if (
//         i === 1 ||
//         i === total ||
//         (i >= current - delta && i <= current + delta)
//     ) {
//       range.push(i);
//     }
//   }
//
//   for (let page of range) {
//     if (last) {
//       if (page - last === 2) {
//         pages.push(last + 1);
//       } else if (page - last > 2) {
//         pages.push('...');
//       }
//     }
//     pages.push(page);
//     last = page;
//   }
//
//   return pages;
// };
const getFixedPagination = (current, total) => {
  // max 5 page numbers total
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  // Near start
  if (current <= 3) {
    return [1, 2, 3, '...', total];
  }

  // Near end
  if (current >= total - 2) {
    return [1, '...', total - 2, total - 1, total];
  }

  // Middle
  return [1, '...', current, '...', total];
};

const COMMENT_PER_PAGE = 20;
const CommentList = observer(({ item, boardType, actionCommentAdd }) => {
  const history = useHistory();

  const usersStore = useUsersStore();
  const commonStore = useCommonStore();
  const communityStore = useTypeStore('communityStore');
  const { wr_comment, comment, wr_id, is_commentable, wo_sub_comment_count } = item;
  const lastPage = Math.max(
    1,
    Math.ceil(wo_sub_comment_count / COMMENT_PER_PAGE)
  );
  const [activeTextarea, toggleTextArea] = useState(false);
  const [text, setText] = useState('');
  const listRef = useRef(null);
  const commentsPerPage = 20;
  const preventSubComment = comment.filter((item) => item['wr_comment_reply'] === "");
  const subComment = comment.filter((item) => item['wr_comment_reply'] !== "");
  const totalPages = Math.ceil(wo_sub_comment_count / commentsPerPage);
  // Initialize to last page (most recent comments)
  const [currentPage, setCurrentPage] = useState(1);
  const prevCountRef = useRef(wo_sub_comment_count);
  const initJumpedRef = useRef(false);
  const newCommentFlagRef = useRef(false);
  const commentAreaRef = useRef(null);
  const shouldScrollToCommentRef = useRef(false);

  const loadComments = (page) => {
    communityStore.getBoardComments({
      wr_id,
      board_type: boardType,
      page,
      limit: COMMENT_PER_PAGE,
    })
  };

  // Initial jump to last page only once
  useEffect(() => {
    if (!initJumpedRef.current && totalPages > 0) {
      setCurrentPage(totalPages);
      initJumpedRef.current = true;
    }
  }, [totalPages]);

  // When new comment is added (count increases), jump to last page
  useEffect(() => {
    if (wo_sub_comment_count > prevCountRef.current) {
      setCurrentPage(totalPages || 1);
      newCommentFlagRef.current = true;
    }
    prevCountRef.current = wo_sub_comment_count;
  }, [wo_sub_comment_count, totalPages]);

  const paginatedComments = preventSubComment.slice(
    (currentPage - 1) * commentsPerPage,
    currentPage * commentsPerPage
  );
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      shouldScrollToCommentRef.current = true;
      if (item?.user?.nick) {
        loadComments(page);
      }
    }
  };

  const getScrollParent = (element) => {
    if (!element) return typeof window !== 'undefined' ? window : null;
    let parent = element.parentElement;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;

      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }

      parent = parent.parentElement;
    }

    return window;
  };

  // Adjust currentPage if it exceeds the new totalPages after a deletion
  useEffect(() => {
    const newTotalPages = Math.ceil(wo_sub_comment_count / commentsPerPage);

    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }

    // If no comments at all, reset to page 1
    if (wo_sub_comment_count === 0) {
      setCurrentPage(1);
    } else if (newTotalPages > 0 && currentPage < newTotalPages) {
      // If comments were added and we're not on the last page, stay on current page
      // But if we're on page 1 and comments were added, move to last page to show newest
      if (currentPage === 1 && newTotalPages > 1) {
        setCurrentPage(newTotalPages);
      }
    }
  }, [wo_sub_comment_count, commentsPerPage]);

  useEffect(() => {
    if (currentPage === totalPages && totalPages > 0 && newCommentFlagRef.current) {
      setTimeout(() => {
        if (listRef.current) {
          // Scroll to comment list element
          listRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else if (typeof window !== 'undefined' && window.scrollTo) {
          // Fallback: scroll window to bottom
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }

        // Highlight the last comment (newly submitted)
        setTimeout(() => {
          if (listRef.current) {
            // Find the last comment element with class "comment"
            const commentElements = listRef.current.querySelectorAll('div.comment');
            if (commentElements.length > 0) {
              const lastComment = commentElements[commentElements.length - 1];
              if (lastComment) {
                // Flash highlight effect
                lastComment.style.transition = 'background-color 0.3s ease';
                lastComment.style.backgroundColor = '#ffffc6';
                setTimeout(() => {
                  lastComment.style.backgroundColor = '';
                  setTimeout(() => {
                    lastComment.style.backgroundColor = '#ffffc6';
                    setTimeout(() => {
                      lastComment.style.backgroundColor = '';
                      setTimeout(() => {
                        lastComment.style.transition = '';
                      }, 300);
                    }, 300);
                  }, 100);
                }, 300);
              }
            }
          }
        }, 300);
      }, 200);
      newCommentFlagRef.current = false;
    }
  }, [currentPage, totalPages, wo_sub_comment_count]);

  useEffect(() => {
    if (!shouldScrollToCommentRef.current) return;
    shouldScrollToCommentRef.current = false;

    if (!commentAreaRef.current) return;

    const OFFSET = 80; // adjust if needed

    // wait for DOM + comments render
    requestAnimationFrame(() => {
      const el = commentAreaRef.current;
      if (!el) return;
      const scrollParent = getScrollParent(el);
      if (!scrollParent) return;

      const elTop =
        el.getBoundingClientRect().top +
        (scrollParent === window ? window.pageYOffset : scrollParent.scrollTop);

      if (scrollParent === window) {
        window.scrollTo({
          top: elTop - OFFSET,
          behavior: 'smooth',
        });
      } else {
        scrollParent.scrollTo({
          top: elTop - OFFSET,
          behavior: 'smooth',
        });
      }
    });
  }, [currentPage]);

  const onClickCommentArea = () => {
    if (commonStore.loginCheck) {
      toggleTextArea(!activeTextarea);
    } else {
      commonStore.modalStore.openConfirmModal({
        message: '로그인 후 사용이 가능 합니다. 로그인 하시겠습니까?',
        okCallback: () => history.push('/auth/login')
      });
    }
  };

  const onChangeTextarea = (e) => {
    if (e.target.value.length > 500) {
      alert('댓글은 500자 까지 가능합니다.');
      return;
    }

    setText(e.target.value);
  };

  const onClickSubmitComment = () => {
    if (text.length === 0) {
      alert('댓글을 입력해주세요.');

      return;
    }

    actionCommentAdd({
      wr_id,
      board_type: boardType,
      comment: text,
      commentType: 'new',
      currentPage
    });

    setText('');
  };

  return (
    <div id={'comment_area'} className="comment_area" ref={commentAreaRef}>
      <h3 className="tit">댓글 {wr_comment}</h3>

      {/* comment_write */}
      {is_commentable && (<div className={clsx('comment_write', activeTextarea && 'selected')}>
        {/* 클릭시 selected 추가 */}
        <form method="post" onSubmit={(e) => e.preventDefault()}>
          <IsLogin
            on={
              <p className="lnk_user">
                <span className={clsx('thumb', `lv${usersStore.me.level}`)}>
                  <img
                    src={usersStore.me.profile_img}
                    onError={(event) => {
                      event.target.src = '/images/thumb_user_def.png';
                    }}
                  />
                  <svg
                    className="border"
                    version="1.1"
                    id="Layer_1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    x="0px"
                    y="0px"
                    viewBox="0 0 36 36"
                    enableBackground="new 0 0 36 36"
                    xmlSpace="preserve"
                  >
                    <path
                      d="M18,2c10.9,0,16,5.1,16,16c0,10.9-5.1,16-16,16C7.1,34,2,28.9,2,18C2,7.1,7.1,2,18,2 M18,0C6,0,0,6,0,18c0,12,6,18,18,18c12,0,18-6,18-18C36,6,30,0,18,0L18,0z" />
                  </svg>
                </span>
                <strong className="name">{usersStore.me.nick}</strong>
              </p>
            }
            off={<p className="lnk_user"></p>}
          />

          <span className="ic_login">로그인 전</span>
          <div className="form_area">
            <textarea
              value={text}
              onChange={onChangeTextarea}
              placeholder="인터넷은 우리가 함께 만들어가는 소중한 공간입니다. 댓글 작성 시 타인에 대한 배려와 책임을 담아주세요."
            ></textarea>
            <div className="txt_byte">
              <span className="num">{text.length}</span>
              <span className="slash">/</span>
              <span className="max">500</span>
            </div>
            <button
              type="submit"
              className="btn_submit"
              onClick={onClickSubmitComment}
            >
              등록
            </button>
          </div>
          <button
            className="btn_open"
            type="button"
            onClick={onClickCommentArea}
          >
            댓글을 남겨 보세요.
          </button>
        </form>
      </div>)}
      {/* // comment_write */}
      <div className="comment_list" ref={listRef}>
        {/*{paginatedComments.length > 0 &&*/}
        {/*    paginatedComments.map((item) => (*/}
        {/*        <CommentRow*/}
        {/*            key={item.wr_id}*/}
        {/*            id={wr_id}*/}
        {/*            boardType={boardType}*/}
        {/*            item={item}*/}
        {/*        />*/}
        {/*    ))*/}
        {/*}*/}
        {preventSubComment.map((mainComment) => {
          const children = subComment.filter(
            (sub) => sub.wr_comment === mainComment.wr_comment
          );

          return (
            <React.Fragment key={mainComment.wr_id}>
              <CommentRow
                id={wr_id}
                boardType={boardType}
                item={mainComment}
              />
              {children.length > 0 && (
                <div className="sub_comments">
                  {children.map((sub) => (
                    <CommentRow
                      key={sub.wr_id}
                      id={wr_id}
                      boardType={boardType}
                      item={sub}
                    />
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/*{totalPages > 1 && (*/}
      {/*    <Pagination>*/}
      {/*      <PageButton*/}
      {/*          onClick={() => goToPage(currentPage - 1)}*/}
      {/*          disabled={currentPage === 1}*/}
      {/*      >*/}
      {/*        이전*/}
      {/*      </PageButton>*/}

      {/*      {getPageRange(currentPage, totalPages).map((page, index) =>*/}
      {/*              page === '...' ? (*/}
      {/*                  <span*/}
      {/*                      key={`dots-${index}`}*/}
      {/*                      style={{ padding: '6px 10px', color: '#999' }}*/}
      {/*                  >*/}
      {/*  …*/}
      {/*</span>*/}
      {/*              ) : (*/}
      {/*                  <PageButton*/}
      {/*                      key={page}*/}
      {/*                      onClick={() => goToPage(page)}*/}
      {/*                      className={clsx({ active: currentPage === page })}*/}
      {/*                  >*/}
      {/*                    {page}*/}
      {/*                  </PageButton>*/}
      {/*              )*/}
      {/*      )}*/}

      {/*      <PageButton*/}
      {/*          onClick={() => goToPage(currentPage + 1)}*/}
      {/*          disabled={currentPage === totalPages}*/}
      {/*      >*/}
      {/*        다음*/}
      {/*      </PageButton>*/}
      {/*    </Pagination>*/}
      {/*)}*/}
      {totalPages > 1 && (
        <Pagination>
          <PageButton
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            이전
          </PageButton>

          {getFixedPagination(currentPage, totalPages).map((page, idx) =>
            page === '...' ? (
              <span
                key={`dots-${idx}`}
                style={{ padding: '6px 6px', minWidth: 20, textAlign: 'center' }}
              >
                …
              </span>
            ) : (
              <PageButton
                key={page}
                onClick={() => goToPage(page)}
                className={clsx({ active: currentPage === page })}
              >
                {page}
              </PageButton>
            )
          )}

          <PageButton
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            다음
          </PageButton>
        </Pagination>
      )}

    </div>
  );
});

export { CommentList };
